<template>
<v-container
  class="overflow-x-hidden overflow-y-auto mt-0 pt-0"
  :style="`height: ${filteredDockerOptions.length > 0 ? Math.min(18 + filteredDockerOptions.length, 22.5) : 10}rem;`"
>
    <v-row>
      <v-col cols="12" md="2">
        <v-card outlined>
          <v-card-text>
            <v-autocomplete outlined hide-details v-model="selectedOption" :items="opts" item-text="option"
              item-value="option" label="Choose Options" @change="onOptionChange" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="10">
        <v-card outlined>
          <v-card-title class="d-flex justify-end ma-0 pa-0">
            <v-col cols="auto">
              <v-text-field outlined dense v-model="search" density="compact" label="Search"
                prepend-inner-icon="mdi-magnify" variant="outlined" hide-details />
            </v-col>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="localIsValid">
            <v-data-table :items-per-page="-1" elevation="0" :hide-default-footer="true" :headers="headers" :items="filteredDockerOptions" item-value="option"
              v-if="filteredDockerOptions.length > 0">
              <template v-slot:item.option="{ item,index }">
                <template v-if="!item.isFromAdmin">
                  <v-label>{{ item.option }}</v-label>
                </template>
                <template v-else>
                  <v-row>
                    <v-col>
                      <v-text-field outlined dense  persistent-hint hide-details v-model="item.option"
                        placeholder="Enter option" @input="changeOption(item, index)" :rules="[requiredRule]"/>
                    </v-col>
                  </v-row>
                </template>
              </template>

              <template v-slot:item.value="{ item, index }">
                <v-row>
                  <v-col>
                    <template v-if="item.allowed_values && item.allowed_values.length > 0">
                      <v-select hide-details outlined dense v-model="item.value" :items="item.allowed_values"
                        :placeholder="getPlaceholder(item)" @input="changeValue(item, index)" :rules="[requiredRule]" />
                    </template>

                    <template v-else-if="!item.pair_values">
                      <v-text-field outlined dense  persistent-hint hide-details v-model="item.value"
                        :placeholder="item.type==='none'?'Argument does not allow any value, you cannot set it.':getPlaceholder(item)"
                        @input="changeValue(item, index)" :disabled="item.type==='none' || item.isToken"
                        :rules="item.type === 'none' || item.isToken?[]:[requiredRule]" />
                    </template>
                    <template v-else-if="item.pair_values">
                      <v-row>
                        <v-col cols="6">
                          <v-text-field outlined dense  persistent-hint hide-details
                            v-model="item.pair_values[0]" :placeholder="'HOST / KEY / PATH'"
                            @input="changePairValue(item, index, true)" :rules="[requiredRule]" />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field outlined dense  persistent-hint hide-details
                            v-model="item.pair_values[1]" :placeholder="'CONTAINER / VALUE / PATH'"
                            @input="changePairValue(item, index, false)" :rules="[requiredRule]" />
                        </v-col>
                      </v-row>
                      <!-- Host paths are relative to the device's home unless ticked: `/tmp/.X11-unix`
                           would otherwise be mounted from `~/tmp/.X11-unix`. -->
                      <v-checkbox v-if="isVolumeOption(item)" dense hide-details class="mt-1"
                        :input-value="!!item.absoluteHostPath" @change="v => setAbsoluteHostPath(item, v)"
                        label="Absolute host path (do not place under the device's home directory)" />
                    </template>
                  </v-col>
                </v-row>
              </template>

              <template v-slot:item.description="{ item, index }">
                <v-text-field outlined dense  persistent-hint hide-details v-model="item.description"
                  placeholder="Enter description" @input="changeDescription(item, index)" />
              </template>

              <template v-slot:item.readOnly="{ item, index }">
                <v-container>
                  <v-checkbox v-model="item.readOnly" @click="onReadOnlyChange(item, index)" dense hide-details />
                </v-container>
              </template>

              <template v-slot:item.actions="{ index }">
                <v-container>
                  <v-btn icon @click="removeOption(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-container>
              </template>
            </v-data-table>
          </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import _ from 'lodash'
export default {
  data () {
    return {
      requiredRule: v => !!v || 'This field is required',
      selectedOption: null,
      selectedOptions: [],
      headers: [
        { text: 'Option', value: 'option' },
        { text: 'Value', value: 'value' },
        { text: 'Description', value: 'description' },
        { text: 'Read Only', value: 'readOnly', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      search: ''
    }
  },
  computed: {
    filteredDockerOptions () {
      return this.dockerOptions.filter(option =>
        option.option.toLowerCase().includes(this.search.toLowerCase()) ||
        (option.description && option.description.toLowerCase().includes(this.search.toLowerCase())) ||
        (option.value && option.value.toLowerCase().includes(this.search.toLowerCase()))
      )
    },
    localIsValid: {
      get () {
        return this.isValid
      },
      set (value) {
        this.$emit('update:isValid', value)
      }
    }
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    dockerOptions: {
      type: Array,
      default: () => []
    },
    opts: {
      type: Array,
      default: () => []
    },
    isValid: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    onOptionChange () {
      if (this.selectedOption) {
        const selectedItem = this.opts.find(
          (opt) => opt.option === this.selectedOption
        )
        if (selectedItem) {
          this.dockerOptions.push({
            ...selectedItem,
            value: selectedItem.value ? selectedItem.value : '',
            readOnly: false,
            pair_values: selectedItem.pair_values ? ['', ''] : false,
            option: selectedItem.option.includes('Add your own') ? '' : selectedItem.option,
            isFromAdmin: !!selectedItem.option.includes('Add your own')
          })
        }
        this.selectedOption = ''
      }
    },

    isVolumeOption (item) {
      return item.option === '-v' || item.option === '--volume'
    },

    // `$set`: rows saved before this flag existed do not have the key, so a plain assignment
    // would not be reactive. The row object is the one in `dockerOptions` (the filter does not
    // clone), so no index lookup is needed.
    setAbsoluteHostPath (item, value) {
      this.$set(item, 'absoluteHostPath', !!value)
    },

    onReadOnlyChange (item, index) {
      this.dockerOptions.map((opt) => {
        if (index === this.dockerOptions.indexOf(opt)) {
          opt.readOnly = item.readOnly
        }
      })
    },

    removeOption (index) {
      this.dockerOptions.splice(index, 1)
    },

    changeOption (item, index) {
      this.dockerOptions.map((opt) => {
        if (index === this.dockerOptions.indexOf(opt)) {
          opt.option = item.option
        }
      })
    },
    changeValue (item, index) {
      this.dockerOptions.map((opt) => {
        if (opt.option === item.option && index === this.dockerOptions.indexOf(opt)) {
          opt.value = item.value
        }
      })
    },
    changePairValue (item, index, isFirst) {
      const option = this.dockerOptions[index]
      if (!option) return
      if (isFirst) {
        option.pair_values[0] = item.pair_values[0]
      } else {
        option.pair_values[1] = item.pair_values[1]
      }
      if (option.pair_values[0] && option.pair_values[1]) {
        option.value = option.pair_values.join(':')
      } else {
        option.value = ''
      }
    },

    changeDescription (item, index) {
      this.dockerOptions.map((opt) => {
        if (opt.option === item.option && index === this.dockerOptions.indexOf(opt)) {
          opt.description = item.description
        }
      })
    },

    getPlaceholder (item) {
      if (item.allowed_values && item.allowed_values.length > 0) {
        return 'Allowed Values: ' + item.allowed_values.join(', ')
      }
      if (item.example) {
        return 'Example: ' + item.example
      }
      if (item.isToken) {
        return 'User will select token.You can not set any value.'
      }
      return 'Enter value'
    }
  },
  watch: {
    dialog: {
      handler: function (newVal) {
        this.dockerOptions = []
      }
    }
  }
}
</script>

<style>
</style>
